import { Divider, Group, Stack } from '@mantine/core';
import { DateTimePicker } from '@mantine/dates';
import { Button, MultiSelect, Select, TextArea } from '@repo/foundations';
import dayjs from 'dayjs';
import { useFormik } from 'formik';
import { noop } from 'lodash-es';
import { useState } from 'react';
import * as Yup from 'yup';
import type { BaseApprovalFormProps } from '../types';

export function GrantPrivilegesToUserDirectForm({
	request,
	allowedPrivileges,
	grantResult,
	isLoadingUsers,
	onSubmit,
	onClose,
	isLoading,
	disabled,
}: BaseApprovalFormProps) {
	const [selectedUser, setSelectedUser] = useState<string | null>(null);
	const [selectedPrivileges, setSelectedPrivileges] = useState<string[]>([]);

	const formik = useFormik({
		initialValues: {
			duration: request.requested_expires_at
				? new Date(request.requested_expires_at)
				: null,
			reason: '',
		},
		validationSchema: Yup.object().shape({
			duration: Yup.date().nullable(),
			reason: Yup.string(),
		}),
		validateOnBlur: true,
		validateOnChange: true,
		onSubmit: async (values) => {
			await onSubmit(
				{
					...values,
					username: selectedUser,
					privileges: selectedPrivileges,
				},
				'grant_privileges_to_user_direct'
			);
		},
	});

	return (
		<form onSubmit={formik.handleSubmit}>
			<Stack spacing="md">
				<Select
					label="Select user"
					placeholder="Choose a user"
					data={
						grantResult?.results?.map((user) => ({
							value: user.name,
							label: `${user.name} ${user.email || ''}`,
						})) || []
					}
					value={selectedUser}
					onChange={setSelectedUser}
					isLoading={isLoadingUsers}
					searchable
					disabled={disabled}
				/>
				<MultiSelect
					label="Select privileges"
					placeholder="Select"
					data={allowedPrivileges.map((privilege) => ({
						value: privilege,
						label: privilege,
					}))}
					value={selectedPrivileges}
					onChange={setSelectedPrivileges}
					required
					setValue={noop} // required by MultiSelect, but onChange handles it
					searchable
					disabled={disabled}
				/>
				<DateTimePicker
					label="Access end date"
					placeholder="Indefinite"
					name="duration"
					minDate={dayjs().toDate()}
					value={formik.values.duration}
					onChange={(durationValue) =>
						formik.setFieldValue('duration', durationValue)
					}
					clearable
					error={formik.errors.duration}
					onBlur={formik.handleBlur}
					popoverProps={{
						withinPortal: true,
					}}
					disabled={disabled}
				/>
				<TextArea
					label="Approval Message"
					placeholder="Optional approval message..."
					name="reason"
					value={formik.values.reason}
					onChange={(event) =>
						formik.setFieldValue('reason', event.currentTarget.value)
					}
					error={formik.errors.reason}
					onBlur={formik.handleBlur}
					minRows={3}
					disabled={disabled}
				/>
			</Stack>
			<Divider my="md" orientation="horizontal" />
			<Group spacing="xs" position="right">
				<Button onClick={onClose} disabled={isLoading || disabled}>
					Cancel
				</Button>
				<Button
					variant="primary"
					type="submit"
					disabled={
						isLoading ||
						!formik.isValid ||
						!selectedUser ||
						selectedPrivileges.length === 0 ||
						disabled
					}
					loading={isLoading}
				>
					{disabled
						? 'Approved'
						: isLoading
							? 'Approving...'
							: 'Approve request'}
				</Button>
			</Group>
		</form>
	);
}
